import {
  clienteAnonimo,
  iniciarClientePostHog,
  resetClientePostHogParaTestes,
} from 'app/services/posthog/posthog-client';
import {
  consumirLogoutEmitido,
  esquecerIdentidadeAplicada,
  identidadeJaAplicada,
  registrarIdentidadeAplicada,
  registrarLogoutEmitido,
} from 'app/services/posthog/posthog-identidade';
import { montarIdentificacaoPostHog } from 'app/services/posthog/posthog-identificacao';
import { PostHogService } from 'app/services/posthog/posthog.service';
import { PostHogEventEnum } from 'enums/posthog-event.enum';

import type { SessionIdentityInput } from './identity-input';

// A fachada não guarda estado (o cliente e o dedupe vivem no módulo), então uma instância
// própria entrega a mesma redação de propriedades sensíveis e o mesmo tratamento de falha
// do lado Angular sem depender da injeção de dependência, que no React não existe.
const posthog = new PostHogService();

/** Carrega o SDK uma única vez por carga de página. Nunca rejeita. */
export function loadAnalytics(): Promise<void> {
  return iniciarClientePostHog().catch(() => undefined);
}

/** Identifica o usuário; não repete um payload já enviado e não faz nada com o SDK inativo. */
export function identifyUser(input: SessionIdentityInput): void {
  // Sem SDK, registrar o payload no dedupe faria um carregamento posterior pular a identificação.
  if (!posthog.isReady()) {
    return;
  }
  const ident = montarIdentificacaoPostHog(input.user, input.tenantId, input.locale);
  if (!ident || identidadeJaAplicada(ident)) {
    return;
  }
  registrarIdentidadeAplicada(ident);
  posthog.identify(ident.distinctId, ident.personProps);
  posthog.register(ident.globalProps);
}

/**
 * Encerra a sessão de analytics: emite o logout atribuído a quem saiu, esquece a identidade
 * aplicada e reseta o SDK reaplicando o ambiente.
 */
export function endAnalyticsSession(): void {
  // O evento sai antes do reset, enquanto o SDK ainda guarda a identidade; o marcador evita que
  // o logout do Angular, executado depois, emita um segundo evento anônimo.
  // Sem identidade aplicada não há quem atribuir o logout, então nem o evento nem o marcador
  // saem; o reset segue sempre chamado porque o guard de anonimato é interno a ele.
  if (posthog.isReady() && !clienteAnonimo()) {
    posthog.capture(PostHogEventEnum.LOGOUT_REALIZADO);
    registrarLogoutEmitido();
  }
  posthog.reset();
}

/** Descarta o estado de módulo entre testes. Não usar em código de produção. */
export function resetAnalyticsForTests(): void {
  resetClientePostHogParaTestes();
  esquecerIdentidadeAplicada();
  consumirLogoutEmitido();
}
